import clsx from "clsx";
import React from "react";
import {
  Box,
  Typography,
  Divider,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { colorSystem } from "../../theme";
import {
  nutritionComments,
  nutritionCommentsLabels,
  nutritionTargetsLabels,
} from "../../constants";
import { tableStyles } from "./tableStyle";
import { usePopupState } from "material-ui-popup-state/hooks";
import { useClient } from "../../hooks/useClient";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import dayjs, { Dayjs } from "dayjs";
import { InternalNutritionEntryDto } from "../../utils/chart";
import { thousandSeparator } from "./utils/thousandSeparator";
import { NutritionEntryDto } from "@growth-machine-llc/stridist-api-client";

const useStyles = makeStyles((theme) => ({
  target: {
    display: "flex",
    padding: theme.spacing(1.2, 0, 0, 1),
    minHeight: 61,
    gap: 2,
    boxShadow: "0px -1px 0px rgba(208, 216, 220, 0.3)",
    position: "relative",
  },

  itemMobile: {
    width: "48.5%",
    margin: theme.spacing(1, 0),
    borderRadius: 4,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    padding: theme.spacing(1.3, 1.3, 2, 1.3),
  },

  targetText: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 700,
  },

  targetTextBold: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: 16,
    [theme.breakpoints.down("lg")]: {
      fontSize: 14,
    },
    [theme.breakpoints.down("md")]: {
      fontSize: 16,
    },
    "& span": {
      fontSize: 14,
      color: theme.palette.text.secondary,
      [theme.breakpoints.down("md")]: {
        fontSize: 16,
      },
    },
  },

  progress: {
    width: "100%",
    maxWidth: "90%",
    marginTop: theme.spacing(2),
  },

  progressTrack: {
    height: 4,
    backgroundColor: colorSystem.secondaryGrayOpacity5,
  },

  targetTextLabel: {
    color: colorSystem.gray,
    fontSize: 14,
    fontWeight: 500,
  },

  button: {
    [theme.breakpoints.down("md")]: {
      position: "absolute",
      bottom: -10,
    },
  },
  comments: {
    paddingLeft: theme.spacing(1),
  },
}));

export interface ClientNutritionEntriesListItemProps {
  entry: NutritionEntryDto;
  refresh: () => void;
  handleAddNutrition?: () => void;
  isProgram?: boolean;
  showAdvancedNutrition: boolean;
}

export function ClientNutritionEntriesListItem(
  props: ClientNutritionEntriesListItemProps,
) {
  const { entry, showAdvancedNutrition } = props;
  const [dirty, setDirty] = React.useState(false);
  const [nutritionEntry, setNutritionEntry] = React.useState({
    carbsGrams: "0.0",
    proteinGrams: "0.0",
    calories: "0",
    fatGrams: "0",
    date: dayjs().format("YYYY-MM-DD"),
  });

  const user = useCurrentUser();
  const client: any = useClient();

  const { breakpoints } = useTheme();

  const roundValue = (value: number) => Math.round(value * 100) / 100;

  const mdUp = useMediaQuery(breakpoints.up("md"));

  const userId = React.useMemo(() => client?.id || user.id, [user, client]);

  const t = tableStyles();
  const s = useStyles();

  return (
    <Box className={clsx(s.target)}>
      <Box className={t.tableTarget}>
        <Typography
          variant="body1"
          children={(entry.date as Dayjs).format("MMM DD, YYYY")}
          className={s.targetText}
        />
      </Box>
      <Box className={t.tableProtein}>
        <div className={s.targetTextBold}>
          {thousandSeparator(Math.round(entry.protein)) || 0}
        </div>
      </Box>
      <Box className={t.tableCarbs}>
        <div className={s.targetTextBold}>
          {thousandSeparator(Math.round(entry.carbs)) || 0}
        </div>
      </Box>
      <Box className={t.tableFat}>
        <div className={s.targetTextBold}>
          {thousandSeparator(Math.round(entry.fat)) || 0}
        </div>
      </Box>
      <Box className={t.tableCalories}>
        <div className={s.targetTextBold}>
          {thousandSeparator(Math.round(entry.calories)) || 0}
        </div>
      </Box>
      {showAdvancedNutrition && (
        <>
          <Divider
            sx={{ height: 30, marginLeft: 1, marginRight: 5 }}
            orientation="vertical"
          />
          <Box className={t.tableCalories}>
            <div className={s.targetTextBold}>
              {entry.alcohol
                ? thousandSeparator(roundValue(entry.alcohol))
                : "-"}
            </div>
          </Box>
          <Box className={t.tableTarget}>
            <div className={s.targetTextBold}>
              {entry.cholesterol
                ? thousandSeparator(roundValue(entry.cholesterol))
                : "-"}
            </div>
          </Box>
          <Box className={t.tableCalories}>
            <div className={s.targetTextBold}>
              {entry.fiber ? thousandSeparator(roundValue(entry.fiber)) : "-"}
            </div>
          </Box>
          <Box className={t.tableCalories}>
            <div className={s.targetTextBold}>
              {entry.sodium ? thousandSeparator(roundValue(entry.sodium)) : "-"}
            </div>
          </Box>
          <Box className={t.tableCalories}>
            <div className={s.targetTextBold}>
              {entry.sugar ? thousandSeparator(roundValue(entry.sugar)) : "-"}
            </div>
          </Box>
          <Box className={t.tableCalories}>
            <div className={s.targetTextBold}>
              {entry.transFat
                ? thousandSeparator(roundValue(entry.transFat))
                : "-"}
            </div>
          </Box>
        </>
      )}
    </Box>
  );
}
